import { api } from './auth';

/**
 * Upload a CSV file for import
 * @param {File} file - The CSV file to upload
 * @returns {Promise} - Response with file_id, headers, preview_rows, total_rows
 */
export const uploadFile = async (file) => {
  const formData = new FormData();
  formData.append('file', file);

  const response = await api.post('/imports/upload', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });

  return response.data;
};

/**
 * Get user's saved mapping preferences
 * @returns {Promise} - Response with mapping object
 */
export const getMappingPreferences = async () => {
  const response = await api.get('/imports/mapping-preferences');
  return response.data;
};

/**
 * Save user's mapping preferences
 * @param {Object} mapping - Field mapping object (CSV column -> profile field)
 * @returns {Promise} - Success message
 */
export const saveMappingPreferences = async (mapping) => {
  const response = await api.post('/imports/mapping-preferences', { mapping });
  return response.data;
};

/**
 * Validate field mapping
 * @param {Object} mapping - Field mapping object to validate
 * @returns {Promise} - Validation result with valid flag and errors array
 */
export const validateMapping = async (mapping) => {
  const response = await api.post('/imports/validate', { mapping });
  return response.data;
};

/**
 * Get preview of mapped contacts
 * @param {string} fileId - The file ID from upload
 * @param {Object} mapping - Field mapping object
 * @returns {Promise} - Response with contacts array and total count
 */
export const getPreview = async (fileId, mapping) => {
  const response = await api.post('/imports/preview', {
    file_id: fileId,
    mapping,
  });
  return response.data;
};

/**
 * Execute the import
 * @param {string} fileId - The file ID from upload
 * @param {Object} mapping - Field mapping object
 * @returns {Promise} - Import results with success_count, skipped_count, errors, duplicates
 */
export const executeImport = async (fileId, mapping) => {
  const response = await api.post('/imports/execute', {
    file_id: fileId,
    mapping,
  });
  return response.data;
};

/**
 * Get user's import history
 * @returns {Promise} - Response with array of import history records
 */
export const getImportHistory = async () => {
  const response = await api.get('/imports/history');
  return response.data;
};

/**
 * Download original CSV file from import history
 * @param {number} historyId - The import history record ID
 * @returns {Promise} - Blob for file download
 */
export const downloadImportFile = async (historyId) => {
  const response = await api.get(`/imports/history/${historyId}/download`, {
    responseType: 'blob',
  });
  
  // Create download link
  const url = window.URL.createObjectURL(new Blob([response.data]));
  const link = document.createElement('a');
  link.href = url;
  
  // Get filename from Content-Disposition header if available
  const contentDisposition = response.headers['content-disposition'];
  let filename = 'import.csv';
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="?(.+)"?/);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }
  
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  
  return response.data;
};

